import React, { useState } from 'react';

interface WordleGameProps {
  onComplete: (score: number) => void;
}

const WORDS = [
  'אהבה', 'שמחה', 'שלום', 'תקווה', 'חברות', 'משפחה', 'אמונה', 'הצלחה', 'בריאות',
  'אושר', 'חופש', 'כבוד', 'נתינה', 'סבלנות', 'אומץ', 'חוזק', 'שקט', 'רוגע',
  'טיול', 'פרח', 'שמש', 'ירח', 'גשם', 'אביב', 'קיץ', 'סתיו', 'חורף', 'ים',
  'הר', 'נחל', 'עץ', 'דשא', 'ציפור', 'כלב', 'חתול', 'בית', 'ספר', 'עט',
  'שולחן', 'כיסא', 'מחשב', 'טלפון', 'מים', 'לחם', 'חלב', 'תפוח', 'תפוז',
  'בננה', 'ענבים', 'אבטיח', 'מלון', 'אפרסק', 'לימון', 'גזר', 'מלפפון', 'עגבניה',
  'צחוק', 'חיוך', 'חיבוק', 'נשיקה', 'מתנה', 'חגיגה', 'מסיבה', 'מוזיקה', 'ריקוד',
  'שיר', 'סיפור', 'סרט', 'משחק', 'צעצוע', 'בובה', 'כדור', 'אופניים', 'רכב',
  'מטוס', 'רכבת', 'אוטובוס', 'סירה', 'חלל', 'כוכב', 'ענן', 'רוח', 'ברק', 'רעם',
  'קשת', 'צבע', 'אדום', 'כחול', 'ירוק', 'צהוב', 'כתום', 'סגול', 'ורוד', 'לבן',
  'שחור', 'אפור', 'חום', 'זהב', 'כסף', 'נחושת', 'ברזל', 'עץ', 'אבן', 'חול'
].filter(w => w.length >= 4 && w.length <= 6); // Filter to ensure fit on mobile

export default function WordleGame({ onComplete }: WordleGameProps) {
  const [targetWord, setTargetWord] = useState('');
  const [guess, setGuess] = useState('');
  const [attempts, setAttempts] = useState<string[]>([]);
  const [finished, setFinished] = useState(false);
  const [win, setWin] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const startGame = () => {
      const randomWord = WORDS[Math.floor(Math.random() * WORDS.length)];
      setTargetWord(randomWord);
      setGuess('');
      setAttempts([]);
      setFinished(false);
      setWin(false);
      setIsActive(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (guess.length !== targetWord.length) return;
    
    // Check if word exists in our (limited) dictionary for validity could be added here
    // For now we trust the user inputs valid Hebrew words or just strings

    const newAttempts = [...attempts, guess];
    setAttempts(newAttempts);
    
    if (guess === targetWord) {
      setFinished(true);
      setWin(true);
      // Reward based on length (difficulty)
      const reward = targetWord.length * 2; // e.g. 4 letters = 8 diamonds, 5=10
      setTimeout(() => onComplete(reward), 1500);
    } else if (newAttempts.length >= 5) {
      setFinished(true); // Fail
      setTimeout(() => onComplete(2), 2000); // Consolation prize
    }
    setGuess('');
  };

  if (!isActive) {
      return (
          <div className="flex flex-col items-center justify-center h-full p-4 text-foreground text-center">
               <h2 className="text-3xl font-bold mb-4">ניחוש מילים</h2>
               <p className="text-muted-foreground mb-8 max-w-xs">
                   נסו לנחש את המילה המסתורית ב-5 ניסיונות בלבד!
                   <br/>
                   ירוק = בול, צהוב = קיים במילה, אפור = לא קיים.
               </p>
               <button 
                  onClick={startGame}
                  className="bg-purple-600 hover:bg-purple-500 text-white px-12 py-4 rounded-full font-bold text-xl shadow-lg transition-transform hover:scale-105"
               >
                   התחל משחק
               </button>
          </div>
      );
  }

  // Fallback if somehow targetWord is missing (shouldn't happen with startGame logic)
  if (!targetWord && isActive) return null;

  return (
    <div className="flex flex-col items-center p-4 text-foreground w-full max-w-sm mx-auto h-full overflow-y-auto">
        <h2 className="text-2xl font-bold mb-4 text-center">ניחוש מילים</h2>
        <p className="mb-4 text-muted-foreground text-sm">נחש את המילה ({targetWord.length} אותיות)</p>
        
        <div className="flex flex-col gap-2 mb-6 w-full items-center">
            {/* Show previous attempts */}
            {attempts.map((att, i) => (
                <div key={i} className="flex gap-1 dir-rtl justify-center">
                    {att.split('').map((char, j) => (
                        <div key={j} className={`w-10 h-10 sm:w-12 sm:h-12 border flex items-center justify-center text-lg font-bold rounded ${
                            targetWord[j] === char ? 'bg-green-500 border-green-500' : targetWord.includes(char) ? 'bg-yellow-500 border-yellow-500' : 'bg-muted border-border'
                        }`}>
                            {char}
                        </div>
                    ))}
                </div>
            ))}
            
            {/* Empty slots for remaining attempts */}
            {Array.from({ length: 5 - attempts.length }).map((_, i) => (
               <div key={`empty-${i}`} className="flex gap-1 dir-rtl justify-center opacity-30">
                  {Array.from({ length: targetWord.length }).map((_, j) => (
                      <div key={j} className="w-10 h-10 sm:w-12 sm:h-12 border border-border rounded"></div>
                  ))}
               </div>
            ))}
        </div>

        {finished && (
            <div className="mb-4 text-xl font-bold">
                {win ? <span className="text-green-400">כל הכבוד!</span> : <span className="text-red-400">המילה הייתה: {targetWord}</span>}
            </div>
        )}

        {!finished && (
            <form onSubmit={handleSubmit} className="flex flex-col items-center gap-2 w-full">
                <input 
                    value={guess}
                    onChange={e => setGuess(e.target.value)}
                    maxLength={targetWord.length}
                    className="bg-muted border-2 border-border p-3 rounded-xl text-center text-xl w-48 focus:border-cyan-400 focus:outline-none tracking-widest"
                    placeholder="הקלד ניחוש..."
                    autoFocus
                />
                <button 
                  type="submit" 
                  disabled={guess.length !== targetWord.length}
                  className="bg-cyan-600 px-8 py-3 rounded-xl font-bold w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  בדוק
                </button>
            </form>
        )}
        
        <div className="mt-4 text-xs text-muted-foreground">
            ניסיון {attempts.length + 1} מתוך 5
        </div>
    </div>
  );
}
